<template>
  <BaseDrawer
    :model-value="open && !isDesktop"
    :title="$t('common.accessibility.mainMenu')"
    max-width-class="sm:max-w-sm"
    full-screen-on-mobile
    @close="handleDialogClose"
  >
    <template #title>
      <Logo
        :to="logoLink"
        wrapper-class="-m-1.5 p-1.5"
        height-class="h-10 sm:h-10"
        @click="closeDrawer"
      />

      <span class="sr-only">{{ $t('common.accessibility.mainMenu') }}</span>
    </template>

    <div class="flow-root">
      <div class="-my-6 divide-base-content/10 divide-y">
        <div
          v-if="isAuthenticated && identity && (primaryMenus.length || userMenus.length)"
          class="py-6 space-y-2"
        >
          <MobileNavLink
            v-for="item in [...primaryMenus, ...userMenus]"
            :key="item.key || item.route || item.label"
            :to="item.route || '#'"
            :label="item.label"
            :icon="item.icon"
            @click="closeDrawer"
          />

          <button
            type="button"
            class="-mx-3 dark:text-danger-400 flex font-semibold gap-x-6 group hover:bg-base-200 items-center p-3 rounded-lg text-base/7 text-danger-600 text-left w-full"
            @click="doLogout"
          >
            <div
              class="bg-base-200 flex flex-none group-hover:bg-base-100 items-center justify-center rounded-lg size-10"
            >
              <ArrowRightOnRectangleIcon
                class="group-hover:text-primary size-5 text-base-content/70"
                aria-hidden="true"
              />
            </div>
            {{ $t('common.actions.signOut') }}
          </button>
        </div>

        <div
          v-if="!isAuthenticated"
          class="py-6 space-y-2"
        >
          <p class="font-semibold pb-1 px-3 text-primary text-xs tracking-wide uppercase">
            {{ $t('common.navigation.forRestaurantsSection') }}
          </p>

          <MobileNavLink
            v-for="item in forRestaurantsSolutions"
            :key="item.name"
            :to="item.href"
            :label="item.name"
            :icon="item.icon"
            @click="closeDrawer"
          />
        </div>

        <div
          v-if="!isAuthenticated"
          class="py-6 space-y-2"
        >
          <p class="font-semibold pb-1 px-3 text-primary text-xs tracking-wide uppercase">
            {{ $t('common.navigation.forCustomersSection') }}
          </p>

          <MobileNavLink
            v-for="item in forCustomersSolutions"
            :key="item.name"
            :to="item.href"
            :label="item.name"
            :icon="item.icon"
            @click="closeDrawer"
          />
        </div>

        <div class="py-6 space-y-2">
          <p
            v-if="!isAuthenticated"
            class="font-semibold pb-1 px-3 text-primary text-xs tracking-wide uppercase"
          >
            {{ $t('common.navigation.explore') }}
          </p>

          <MobileNavLink
            v-for="item in links.filter((l) => l.name !== 'Home')"
            :key="item.name"
            :to="item.href"
            :label="item.name"
            :icon="item.icon"
            @click="closeDrawer"
          />
        </div>
      </div>
    </div>

    <slot name="extraMobileFilters" />
  </BaseDrawer>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import { ArrowRightOnRectangleIcon } from '@heroicons/vue/24/outline'
import MobileNavLink from '~/components/MobileNavLink.vue'
import BaseDrawer from '~/components/shared/dialogs/BaseDrawer.vue'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { landingGuestExploreLinks } from '~/utils/landingGuestExploreLinks'
import { useNavigationSolutions } from '~/composables/useNavigationSolutions'
import { useNavAuthMenu } from '~/composables/useNavAuthMenu'
import { useLogoLink } from '~/composables/useLogoLink'

const props = withDefaults(
  defineProps<{
    /** Controls whether the drawer is open. */
    open?: boolean
  }>(),
  { open: false }
)

const emit = defineEmits<{ close: [] }>()

defineSlots<{
  extraMobileFilters?: () => unknown
}>()

// A viewport wide enough for the full navigation has no use for this drawer.
const isDesktop = useMediaQuery('(min-width: 1024px)')

watch(isDesktop, (desktop) => {
  if (desktop && props.open) emit('close')
})

const { identity, isAuthenticated } = useIdentity()
const { showAddToCart: showCart } = useShowAddToCart()
const { resetNotifications } = useNotificationsNav({ enabled: showCart })

const { primaryMenus, userMenus, handleLogout } = useNavAuthMenu()
const { logoLink } = useLogoLink()

const links = landingGuestExploreLinks
const forRestaurantsSolutions = useNavigationSolutions('restaurants')
const forCustomersSolutions = useNavigationSolutions('customers')

const closeDrawer = () => {
  setTimeout(() => {
    emit('close')
  }, 150)
}

const handleDialogClose = () => {
  emit('close')
}

const doLogout = () => {
  void handleLogout(async () => {
    await resetNotifications()
  })
  closeDrawer()
}
</script>
